import React, { useEffect, useState } from 'react';
import { Lock, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/supabaseClient';
import PublicWhatsAppSupportButton from './PublicWhatsAppSupportButton.jsx';

// Tela pública de "Convite Link": o aluno abre /courses/{courseId}?turma={classId}
// SEM login, informa o código de 4 dígitos e entra direto no curso.
// A validação roda na Edge Function `class-invite-login`, que devolve um
// `token_hash` que vira sessão do aluno via verifyOtp.
export default function ClassInviteAccessView({ courseId, classId, whatsappSupport, authenticated = false, onAuthenticated }) {
  const [context, setContext] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!courseId || !classId) return;
      try {
        const { data } = await supabase.rpc('get_class_invite_context', {
          p_course_id: courseId,
          p_class_id: classId,
        });
        if (!cancelled && data) setContext(data);
      } catch {
        // O nome do curso/turma é apenas informativo; a validação ocorre no envio.
      }
    };
    load();
    return () => { cancelled = true; };
  }, [courseId, classId]);

  const onDigit = (value) => {
    setError('');
    setCode(String(value || '').replace(/\D/g, '').slice(0, 4));
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    const cleaned = String(code || '').replace(/\D/g, '').slice(0, 4);
    if (cleaned.length !== 4) {
      setError('Informe o número da sua matrícula.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      // Sempre usa a edge function: ela valida o código por class_id + course_id +
      // code (sem depender de quem está logado) e emite a sessão do DONO do código.
      // Isso garante o re-login mesmo quando a sessão atual é de outra conta
      // (ex.: admin testando o link de um aluno).
      const res = await fetch(`${SUPABASE_URL}/functions/v1/class-invite-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ course_id: courseId, class_id: classId, code: cleaned }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.token_hash) {
        setError(data?.message || 'Não foi possível validar o número da matrícula.');
        return;
      }

      const { error: otpError } = await supabase.auth.verifyOtp({
        type: 'magiclink',
        token_hash: data.token_hash,
      });
      if (otpError) {
        setError('Não foi possível iniciar sua sessão. Tente novamente.');
        return;
      }

      if (typeof onAuthenticated === 'function') onAuthenticated();
    } catch {
      setError('Falha de conexão. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const courseTitle = context?.course_title || '';
  const className = context?.class_name || '';

  return (
    <>
      <div className="min-h-[calc(100vh-72px)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <span className="bg-gradient-to-r from-orange-500 to-amber-400 bg-clip-text text-transparent font-black text-3xl tracking-tighter">SOFERA</span>
            <span className="text-slate-300 font-bold text-xs tracking-[0.2em] ml-2">| Evolução Inteligente</span>
          </div>

          <form
            onSubmit={onSubmit}
            className="bg-slate-900/70 border border-slate-800 rounded-2xl shadow-2xl p-6 md:p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400">
                <Lock size={20} />
              </div>
              <div className="flex flex-col">
                <h1 className="text-xl font-black text-white leading-tight">Acesso por matrícula</h1>
                <p className="text-sm text-slate-400">
                  {courseTitle || 'Acesse o curso com o número da sua matrícula.'}
                </p>
              </div>
            </div>

            {className && (
              <p className="text-slate-500 text-xs mb-5">Turma: {className}</p>
            )}

            <p className="text-slate-400 text-sm mb-5">
              Insira o número da sua matrícula para liberar este curso.
            </p>

            <div className="flex justify-center">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                autoFocus
                value={code}
                onChange={(e) => onDigit(e.target.value)}
                placeholder="0000"
                className="w-40 text-center text-3xl font-black tracking-[0.5em] bg-slate-950/70 border border-slate-800 rounded-xl py-3 text-white outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-500/30"
              />
            </div>

            {error && (
              <p className="mt-4 text-center text-rose-400 text-xs font-medium">{error}</p>
            )}

            <div className="pt-5">
              <button
                type="submit"
                disabled={submitting || code.length !== 4}
                className="w-full h-11 flex items-center justify-center gap-2 rounded-xl bg-orange-500 text-white font-black hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                {submitting ? 'Validando…' : 'Liberar acesso'}
              </button>
            </div>

            <div className="mt-5 flex items-center justify-center gap-2 text-[11px] text-slate-500">
              <ShieldCheck size={14} className="text-slate-600" />
              Acesso individual e vinculado ao seu cadastro.
            </div>
          </form>
        </div>
      </div>

      <PublicWhatsAppSupportButton
        whatsappNumber={whatsappSupport?.number}
        enabled={Boolean(whatsappSupport?.enabled)}
        message="Olá! Preciso de ajuda para acessar meu curso pela matrícula."
      />
    </>
  );
}
